import React, {useState} from 'react';
import {Box, Button, Checkbox, FormControlLabel, Stack, Typography} from '@mui/material';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import {mergeArraysByKey} from '../../../../utility/mergeArraysByKey';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import SettingsSectionBase from '../../Forms/PrivateFormSettings/SettingsSection';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';

/* eslint-disable react/prop-types */

const groupFiltersTableColumns = [
    {
        accessorKey: 'displayName',
        header: 'Group Name',
        grow: true,
    },
    {
        accessorKey: 'excluded',
        header: 'Status',
        size: 50,
        grow: false,
        Cell: ({cell}) => cell.getValue() === true ? 'Excluded' : 'Included'
    }
];

const FiltersTab = ({
                        data,
                        settings,
                        strictRecipientsFiltering,
                        setStrictRecipientsFiltering,
                        setSettings,
                        disableStrictRecipientsFiltering = false,
                        disableEnquirySelect = false,
                        hideStudentSelect = false,
                        hideApplicationSelect = false,
                        memberType
                    }) => {
    const [filter, setFilter] = useState({});
    const isFilterNotSelected = filter == null || !filter.filterId;
    const rootFilterIds = data ? data.map(x => x.id) : [];

    const handleAddGroupFilter = (isExcluded) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(x => x.name).join(' - '),
            excluded: isExcluded
        };

        setSettings(prev => ({
            ...prev,
            groupFilters: mergeArraysByKey([...settings.groupFilters, newFilter], settings.groupFilters)
        }));
        setFilter(null);
    };
    const memberTypeTitle =
        memberType === GROUP_MEMBERS_TYPE.employee ? 'Employee' : 'Student';

    const icon =
        memberTypeTitle === 'Employee'
            ? <AssignmentIndOutlinedIcon fontSize="small"/>
            : memberTypeTitle === 'Student'
                ? <PeopleOutlineOutlinedIcon fontSize="small"/>
                : <Groups2OutlinedIcon fontSize="small"/>;

    const plural =
        memberTypeTitle === 'Employee'
            ? 'Employees'
            : memberTypeTitle === 'Student'
                ? 'Students'
                : 'Members';

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <SettingsSectionBase
                header={`${memberTypeTitle} Selection`}
                headerIcon={icon}
                subHeader={`Select ${memberTypeTitle.toLowerCase()} groups and/or individual ${plural.toLowerCase()} to report on.`}
            >

                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="subtitle1" fontWeight="bold">{memberTypeTitle} Groups</Typography>
                    <Box>
                        <AdmicityGroupFilterSelect
                            isRequired={!settings.studentFilters.length && !settings.applicationFilters.length}
                            sx={{width: '100%'}}
                            data={data}
                            value={filter?.filterId || ''}
                            onChange={(filterId, paths) => setFilter({filterId, paths})}
                        />
                        <Stack alignItems="center" flexDirection="row" gap={1} justifyContent={'end'} pt={1}>
                            <Button
                                onClick={() => handleAddGroupFilter(true)}
                                variant="outlined"
                                disabled={isFilterNotSelected || rootFilterIds.includes(filter.filterId)}
                            >
                                Exclude
                            </Button>
                            <Button
                                onClick={() => handleAddGroupFilter(false)}
                                variant="contained"
                                disabled={!filter?.filterId}
                                sx={{
                                    backgroundColor: '#186BBE',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Include
                            </Button>
                        </Stack>
                    </Box>
                </Box>
                {
                    settings.groupFilters?.length > 0 && <Box>
                        <AdmicityTableV2
                            columns={groupFiltersTableColumns}
                            data={settings.groupFilters}
                            tableProps={{
                                layoutMode: 'grid-no-grow',
                                enableRowActions: true,
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                muiTableBodyProps: {
                                    sx: {
                                        '& :last-child td': {
                                            borderBottom: 0,
                                        },
                                    }
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        maxHeight: 150,
                                        minHeight: 'auto',
                                        border: 'none'
                                    },
                                    style: {
                                        minHeight: 150,
                                    },
                                },
                                renderRowActions: ({row}) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const filters = settings.groupFilters.filter(filter => row.original.id !== filter.id);

                                            setSettings(prev => ({
                                                ...prev,
                                                groupFilters: filters
                                            }));
                                        }}>
                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                    </IconButton>
                                )
                            }}
                        />
                        <FormControlLabel
                            sx={{
                                marginBottom: -2,
                            }}
                            disabled={disableStrictRecipientsFiltering}
                            control={
                                <Checkbox
                                    checked={strictRecipientsFiltering}
                                    onChange={(e) => setStrictRecipientsFiltering(e.target.checked)}
                                />
                            }
                            label="Must appear in all these groups"
                        />
                    </Box>
                }
                <Box display="flex" flexDirection="column" gap={2}>
                    {memberType === GROUP_MEMBERS_TYPE.employee
                        ? (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Individual Employees
                                </Typography>
                                <PaginatedAutocomplete
                                    textFieldProps={{
                                        label: 'Employees',
                                        placeholder: 'Enter name, surname, or full name',
                                        required: true,
                                    }}
                                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                                    getOptionLabel={(option) => option.label || `${option.preferredForename} ${option.preferredSurname}`}
                                    value={settings.employeeFilters || []}
                                    onChange={(e, newValue) =>
                                        setSettings((prev) => ({...prev, employeeFilters: newValue}))
                                    }
                                />
                            </>
                        )
                        : (
                            <>
                                {!hideApplicationSelect && (
                                    <>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Individual Students
                                        </Typography>
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Applications',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'applicant',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            value={settings.applicationFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, applicationFilters: newValue}))
                                            }
                                        />
                                    </>
                                )}
                                    {!hideStudentSelect && (
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Students',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'student',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            value={settings.studentFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, studentFilters: newValue}))
                                            }
                                        />
                                    )}
                                    {!disableEnquirySelect && (
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Enquiries',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'enquiry',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            value={settings.enquiryFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, enquiryFilters: newValue}))
                                            }
                                        />
                                    )}
                            </>
                        )}
                </Box>
            </SettingsSectionBase>
        </Box>
    );
};

export default FiltersTab;